/**
 * Shared transcript logic — used by the server (markdown export, stats)
 * and the client (turn rendering). Pure functions, no runtime dependencies.
 */

export interface ScribeWord {
  text: string;
  start: number;
  end: number;
  type: "word" | "spacing" | "audio_event" | string;
  speaker_id?: string;
  logprob?: number;
}

export interface ScribeResponse {
  language_code: string;
  language_probability: number;
  text: string;
  words: ScribeWord[];
  audio_duration_secs?: number;
}

export interface Turn {
  speakerId: string;
  start: number;
  words: ScribeWord[];
}

const TURN_GAP_SECS = 1.5;

export function groupTurns(words: ScribeWord[]): Turn[] {
  const turns: Turn[] = [];
  let current: Turn | null = null;
  for (const w of words) {
    // Tokens without a speaker (spacing, some audio events) belong to the current turn.
    const sid = w.speaker_id ?? current?.speakerId ?? "";
    const lastEnd = current?.words.at(-1)?.end;
    const needsNew =
      !current
      || current.speakerId !== sid
      || (w.type === "word" && lastEnd != null && w.start - lastEnd > TURN_GAP_SECS);
    if (needsNew) {
      const fallback = current ? current.start : w.start;
      const next: Turn = { speakerId: sid, start: w.type === "word" ? w.start : fallback, words: [] };
      turns.push(next);
      current = next;
    }
    current!.words.push(w);
  }
  return turns.filter((t) => t.words.some((w) => w.type === "word"));
}

export function speakerDisplay(speakerId: string, labels: Record<string, string>): string {
  if (labels[speakerId]) return labels[speakerId]!;
  if (!speakerId) return "Unknown";
  const m = speakerId.match(/(\d+)$/);
  if (m) return `Speaker ${m[1]}`;
  return speakerId;
}

export interface TranscriptStats {
  wordCount: number;
  speakerCount: number;
}

/** Word/speaker counts from the raw response, falling back to plain text. */
export function transcriptStats(raw: ScribeResponse | null, text: string | null): TranscriptStats {
  if (raw?.words?.length) {
    let wordCount = 0;
    const speakers = new Set<string>();
    for (const w of raw.words) {
      if (w.type !== "word") continue;
      wordCount++;
      if (w.speaker_id) speakers.add(w.speaker_id);
    }
    return { wordCount, speakerCount: speakers.size };
  }
  const trimmed = text?.trim();
  if (!trimmed) return { wordCount: 0, speakerCount: 0 };
  return { wordCount: trimmed.split(/\s+/).length, speakerCount: 0 };
}

export function fmtClock(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function turnText(words: ScribeWord[]): string {
  let out = "";
  for (const w of words) {
    if (w.type === "audio_event") {
      const t = w.text.trim();
      out += t ? `*${t}*` : "";
    } else {
      out += w.text;
    }
  }
  return out.trim().replace(/\s+/g, " ");
}

export interface MarkdownSource {
  title: string;
  filename: string;
  createdAt: number;
  durationSecs: number | null;
  languageCode: string | null;
  summary: string | null;
  speakerLabels: Record<string, string>;
  raw: ScribeResponse | null;
  text: string | null;
}

/** Render a transcription as a self-contained markdown document. */
export function buildMarkdown(src: MarkdownSource): string {
  const lines: string[] = [];
  lines.push(`# ${src.title}`);
  lines.push("");

  const turns = src.raw?.words?.length ? groupTurns(src.raw.words) : [];
  const stats = transcriptStats(src.raw, src.text);
  const facts: string[] = [];
  facts.push(new Date(src.createdAt).toISOString().slice(0, 10));
  if (src.durationSecs != null) facts.push(fmtClock(src.durationSecs));
  if (src.languageCode) facts.push(src.languageCode.toUpperCase());
  if (stats.wordCount > 0) facts.push(`${stats.wordCount.toLocaleString("en-US")} words`);
  if (stats.speakerCount > 0) facts.push(`${stats.speakerCount} speaker${stats.speakerCount === 1 ? "" : "s"}`);
  facts.push(src.filename);
  lines.push(facts.join(" · "));
  lines.push("");

  if (src.summary?.trim()) {
    lines.push("## Summary");
    lines.push("");
    lines.push(src.summary.trim());
    lines.push("");
  }

  lines.push("## Transcript");
  lines.push("");

  if (turns.length > 0) {
    for (const turn of turns) {
      const speaker = speakerDisplay(turn.speakerId, src.speakerLabels);
      lines.push(`**${speaker}** (${fmtClock(turn.start)})`);
      lines.push("");
      lines.push(turnText(turn.words));
      lines.push("");
    }
  } else if (src.text?.trim()) {
    lines.push(src.text.trim());
    lines.push("");
  } else {
    lines.push("*No transcript text available.*");
    lines.push("");
  }

  return lines.join("\n");
}

/** Filesystem-safe markdown filename derived from the title (unicode kept). */
export function markdownFilename(title: string, fallback: string): string {
  const slug = title
    .normalize("NFC")
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `${slug || fallback}.md`;
}
